import { useCallback, useContext } from 'react'

import { UserContext } from '@editor/contexts/Users'
import { FileProcessingStatus } from '@editor/interfaces/file'
import { InspectionItem } from '@editor/interfaces/inspection'
import { PolygonDetectionResult, Shape, ShapeDetectionResult, ShapeKey, ShapeKeyType } from '@editor/interfaces/shape'
import { useInspectionItems } from '@editor/pages/projects/hooks/useInspectionItems'
import { useShapes } from '@editor/pages/projects/hooks/useShapes'
import { ERROR_PROCESS_MESSAGE } from '@editor/services/ErrorHandler'
import { convertDetectionResultToShapes } from '@editor/services/Points'
import { pollAsyncJobStatus } from '@editor/services/VolumeEstimation'
import { RootState, useAppDispatch } from '@editor/store/app'
import { setSelectedElementIds } from '@editor/store/tool'
import mixpanel from 'mixpanel-browser'
import { useSelector } from 'react-redux'

export default function useDetectPolygonsVolume(
  /**
   * Shape key to be detected.
   */
  shapeKey: ShapeKeyType,
) {
  // Context
  const { getAccessToken } = useContext(UserContext)

  // Hooks
  const { fetchShapes } = useShapes()
  const { fetchInspectionItems } = useInspectionItems()

  // Store
  const dispatch = useAppDispatch()
  const inspectionSheet = useSelector((state: RootState) => state.inspectionItems.inspectionSheet)
  const project = useSelector((root: RootState) => root.page.project)
  const inspectionArea = useSelector((root: RootState) => root.page.inspectionArea)

  /**
   * Polls the server for completion of volume estimation.
   */
  const poll: (jobToken: string) => Promise<boolean> = useCallback(
    async (jobToken: string) => {
      if (
        !project ||
        inspectionArea?.downsample_status?.status !== FileProcessingStatus.SUCCEEDED ||
        !jobToken ||
        !inspectionSheet
      )
        return false

      let successShapesCount = 0
      let totalErrorShapes = 0
      try {
        const token = await getAccessToken()
        if (!token) return false

        const errorResults: ShapeDetectionResult[] = []
        const jobResults = await pollAsyncJobStatus<{
          error_object: string
          shape_type: ShapeKey
          list_shapes: PolygonDetectionResult[]
          inspection_items: InspectionItem[]
        }>(token, jobToken)
        const results = jobResults.list_shapes as ShapeDetectionResult[]

        // Job failed or no shapes detected
        if (!results.length) {
          const statusId = jobResults?.error_object
            ? (
                JSON.parse(
                  (
                    JSON.parse((JSON.parse(jobResults.error_object) as { body: string }).body) as {
                      status_message: string
                    }
                  ).status_message,
                ) as { status_id: number }
              ).status_id
            : null
          const message = statusId ? ERROR_PROCESS_MESSAGE.ESTIMATE_VOLUME.messages[statusId] || '' : ''
          throw new Error(message || '要素は検出されませんでした。')
        }

        // Parse results
        const newShapes = convertDetectionResultToShapes(
          shapeKey,
          results.filter((shape) => !shape.error_id),
        ) as Shape[]
        errorResults.push(...(results.filter((shape) => !!shape.error_id) || []))
        totalErrorShapes = errorResults.length
        successShapesCount = results.length - totalErrorShapes

        // if job has failed or no shape has been detected, show a message
        if (totalErrorShapes) {
          throw new Error(`${totalErrorShapes}個の要素が検出されませんでした。`)
        }

        if (newShapes.length > 0) {
          // refetch inspection sheet items as various behavior related to estimate volume
          // depends on inspection item generated by estimateVolume API call
          // do this regardless of errors since if some succeeds, those need to be shown to user
          await Promise.all([fetchInspectionItems(), fetchShapes()])

          // set the new planes as selected planes
          dispatch(setSelectedElementIds(newShapes.map((shape) => shape.shape_id)))
        }

        return true
        // no catch here, let it go to outer catch
      } finally {
        // track with mixpanel
        mixpanel.track('Finish detecting shapes', {
          'Shape type': shapeKey,
          'Number of shapes (success)': successShapesCount,
          'Number of shapes (error)': totalErrorShapes,
        })
      }
    },
    [project, inspectionArea, inspectionSheet, shapeKey, dispatch, fetchShapes, getAccessToken, fetchInspectionItems],
  )

  return {
    poll,
  }
}
